import {
    ADD_NEW_MEMBER,
    GET_EXISTING_MEMBER,
    REMOVE_MEMBERS,
    UPDATE_MEMBER,
    REMOVE_PENDING,
    SUSPEND_PROPERTY_GUEST,
    TRANSFER_MEMBER,
    ADVANCE_MEMBER_QUERY
} from '../actions/types';

import { all, fork, takeLatest, put, call, select } from 'redux-saga/effects';
import { collection, query, where, getDocs } from 'firebase/firestore';

import { db } from 'config/firebase';

import {
    setExistingMember,
    addingNewMember,
    addingNewMemberSuccess,
    addingNewMemberFailure,
    transferMemberFailure,
    transferMemberSuccess,
    queryMemberDataSuccess,
    queryMemberDataFailure
} from '../actions/Members';

import { responses, userRoles, userTypes } from 'utils/constants';
import * as selectors from './Selectors';
import { openSnackbar } from 'store/actions/Snackbar';
import { log } from 'utils/Loggers';
import OrgPropertiesService from '../../services/org-properties.service';

const userCollectionRef = collection(db, 'users');
const accesKeysCollectionRef = collection(db, 'access_keys');
const devicesCollectionRef = collection(db, 'devices');

const inviteErrorMessage = error => {
    const reason = error?.response?.data?.reason || error?.reason;
    return (
        responses.invite?.[reason] ||
        error?.response?.data?.message ||
        error?.message ||
        'Member was not added successfully.'
    );
};

const permissionsToArray = permissions =>
    Array.isArray(permissions)
        ? permissions
        : Object.keys(permissions || {}).filter(permission => permissions[permission]);

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////// Add/Invite Members ////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const existingByEmail = async email => {
    const q = query(userCollectionRef, where('email', '==', email));

    return getDocs(q)
        .then(querySnapshot => {
            if (querySnapshot.empty) {
                return [];
            }

            const emailExist = [];
            querySnapshot.forEach(doc => {
                emailExist.push(doc.data());
            });

            return emailExist;
        })
        .catch(error => {
            throw error;
        });
};

const existingByPhone = async phone => {
    const q = query(userCollectionRef, where('phone_number', '==', phone));

    return getDocs(q)
        .then(querySnapshot => {
            if (querySnapshot.empty) {
                return [];
            }

            const phoneExist = [];
            querySnapshot.forEach(doc => {
                phoneExist.push(doc.data());
            });

            return phoneExist;
        })
        .catch(error => {
            throw error;
        });
};

const checkExistingMemberRequest = async member => {
    const { phone, email } = member;

    try {
        const emailExistence = email ? await existingByEmail(email) : [];
        const phoneExistence = phone ? await existingByPhone(phone) : [];

        const existence = [...emailExistence, ...phoneExistence];
        const filteredExistence = existence.reduce((reducedExistence, current) => {
            if (!reducedExistence.some(x => x.uid === current.uid)) {
                reducedExistence.push(current);
            }
            return reducedExistence;
        }, []);

        return { existing: filteredExistence };
    } catch (error) {
        return { error };
    }
};

export function* checkExistingMember({ payload }) {
    const { member } = payload;
    const { existing, error } = yield call(() =>
        checkExistingMemberRequest({ email: member.email, phone: member.phone_number })
    );
    if (existing) {
        if (existing.length) {
            yield put(setExistingMember(existing));
        } else {
            yield put(
                addingNewMember({
                    member,
                    exist: false
                })
            );
        }
    } else {
        // Error Handling with put and maybe UI message
        console.error(
            'Check Existing Error: error checking for existing member when adding',
            {
                error
            }
        );
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
//////////////////////////////////////////////////// Add/Invite Members ////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const addNewMemberRequest = async ({ invite, org }) => {
    const propertyId = invite.address.id;
    const orgId = org.org_id;

    await OrgPropertiesService.createUserInvite(orgId, propertyId, {
        first_name: invite.first_name,
        last_name: invite.last_name,
        email: invite.email || null,
        phone: invite.phone || null,
        role: userRoles.RESIDENT,
        type: invite.type || userTypes.MEMBER,
        permissions: permissionsToArray(invite.permissions),
        rental: invite?.rental || false,
        primary: invite?.primary || false,
        minor: invite?.minor || false,
        requires_approval: false
    });

    return { res: true };
};

const addExistingMemberRequest = async ({ member, org, exist }) => {
    const orgId = org.org_id;
    const propertyId = member.address.id;

    await OrgPropertiesService.addExistingUser(orgId, propertyId, exist.uid, {
        first_name: member.first_name || exist.first_name,
        last_name: member.last_name || exist.last_name,
        email: member.email || exist.email || null,
        phone: member.phone || exist.phone || null,
        role: userRoles.RESIDENT,
        type: member.type || userTypes.MEMBER,
        permissions: member.permissions,
        rental: member?.rental || false,
        primary: member?.primary || false,
        minor: member?.minor || false,
        requires_approval: false
    });

    return { res: true };
};

export function* addMember({ payload }) {
    const { member, exist } = payload;
    const userData = yield select(selectors._userData);
    const activeOrg = yield select(selectors._activeOrg);
    const selectedOrg = yield select(selectors._selectedOrg);

    const org = activeOrg || selectedOrg;

    try {
        const { res } = !exist
            ? yield call(() => addNewMemberRequest({ invite: member, org }))
            : yield call(() =>
                  addExistingMemberRequest({
                      member,
                      org,
                      exist: exist[0]
                  })
              );

        if (res) {
            yield put(addingNewMemberSuccess());
            yield put(
                openSnackbar({
                    open: true,
                    message: 'Success: Invite sent, pending acceptance.',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    }
                })
            );
        }
    } catch (error) {
        yield put(addingNewMemberFailure(error));
        yield put(
            openSnackbar({
                open: true,
                message: `Failed: ${inviteErrorMessage(error)}`,
                variant: 'alert',
                alert: {
                    color: 'error'
                }
            })
        );
        log(`Error: error adding ${exist ? 'existing' : 'new'} member (FS)`, {
            error,
            member,
            exist,
            userData,
            org
        });
    }
}

export function* removeMemberRequest({ member, orgId, propertyId }) {
    yield call(() =>
        OrgPropertiesService.removeUserAccess(orgId, propertyId, member.uid)
    );
    return { res: true };
}

export function* removeMember({ payload }) {
    const { member, orgId, propertyId } = payload;
    try {
        const { res } = yield call(() =>
            removeMemberRequest({ member, orgId, propertyId })
        );
        if (res) {
            yield put(
                openSnackbar({
                    open: true,
                    message: 'Success: Member has been removed!',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    }
                })
            );
        }
    } catch (error) {
        yield put(
            openSnackbar({
                open: true,
                message: `Failed: Member was not removed successfully.`,
                variant: 'alert',
                alert: {
                    color: 'error'
                }
            })
        );
        log(`Error: error removing members (FS/RTDB)`, {
            error,
            member,
            orgId,
            propertyId
        });
    }
}

const updateMemberRequest = async ({ member, orgId, propertyId }) => {
    await OrgPropertiesService.updateUser(orgId, propertyId, member.uid, {
        first_name: member.first_name.toLowerCase().trim(),
        last_name: member.last_name.toLowerCase().trim(),
        phone: member.phone,
        type: member.type,
        primary: member.primary,
        minor: member.minor,
        suspended: member?.suspended ?? false,
        permissions: member.permissions
    });

    if (member.emailChange) {
        await OrgPropertiesService.changeEmailAddress(orgId, propertyId, member.uid, {
            email: member.email.toLowerCase().trim()
        });
    }

    return { res: true };
};

export function* updateMember({ payload }) {
    const { member, orgId, propertyId } = payload;
    try {
        const { res } = yield call(() =>
            updateMemberRequest({ member, orgId, propertyId })
        );
        if (res) {
            yield put(
                openSnackbar({
                    open: true,
                    message: 'Success: Member has been updated!',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    }
                })
            );
        }
    } catch (error) {
        yield put(
            openSnackbar({
                open: true,
                message: `Failed: Member was not updated successfully.`,
                variant: 'alert',
                alert: {
                    color: 'error'
                }
            })
        );
        log(`Error: error updating member (FS/RTDB)`, {
            error,
            member,
            orgId,
            propertyId
        });
    }
}

function* transferMemberRequest({ member, fromPropertyId, toPropertyId, orgId }) {
    try {
        yield call(() =>
            OrgPropertiesService.transferUser(orgId, fromPropertyId, member.uid, {
                to_property_id: toPropertyId
            })
        );
        return { res: true };
    } catch (error) {
        console.error('Error during member transfer:', error);
        throw error;
    }
}

export function* transferMemberSaga({ payload }) {
    const { member, fromPropertyId, toPropertyId, orgId } = payload;

    try {
        const { res } = yield call(() =>
            transferMemberRequest({ member, fromPropertyId, toPropertyId, orgId })
        );

        if (res) {
            yield put(transferMemberSuccess());
            yield put(
                openSnackbar({
                    open: true,
                    message: 'Member transferred successfully',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    }
                })
            );
        }
    } catch (error) {
        console.error(error);
        yield put(transferMemberFailure(error));
        yield put(
            openSnackbar({
                open: true,
                message: 'Failed to transfer member',
                variant: 'alert',
                alert: {
                    color: 'error'
                }
            })
        );
    }
}

const removePendingRequest = async ({ pending }) => {
    try {
        const byPropertyId = pending.reduce((grouped, invite) => {
            const propertyId = invite.property_id;
            if (!propertyId) return grouped;
            return {
                ...grouped,
                [propertyId]: [...(grouped[propertyId] || []), invite.invite_id]
            };
        }, {});

        await Promise.all(
            Object.entries(byPropertyId).map(([propertyId, ids]) =>
                OrgPropertiesService.deleteInvites(pending[0].org_id, propertyId, ids)
            )
        );

        return { res: true, error: [] };
    } catch (error) {
        console.error('Error removing pending invites:', error);

        return { res: false, error: error.message };
    }
};

export function* removePending({ payload }) {
    const { pending } = payload;
    const { res, error } = yield call(() => removePendingRequest({ pending }));
    if (res) {
        yield put(
            openSnackbar({
                open: true,
                message: `${pending.length} ${
                    pending.length === 1 ? 'pending invite was' : 'pending invites were'
                } successfully deleted`,
                variant: 'alert',
                alert: {
                    color: 'success'
                }
            })
        );
    } else {
        yield put(
            openSnackbar({
                open: true,
                message: `Failed to delete pending invites: ${error}`,
                variant: 'alert',
                alert: {
                    color: 'error'
                }
            })
        );
        log(`Error: error removing pending (FS/RTDB)`, {
            error,
            pending
        });
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////// Suspend Property Guest //////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const suspendPropertyGuestRequest = async ({ member, orgId, propertyId }) => {
    await OrgPropertiesService.updateUser(orgId, propertyId, member.uid, {
        suspended: member.suspended
    });

    return { res: true };
};

export function* suspendPropertyGuest({ payload }) {
    const { member, orgId, propertyId } = payload;
    try {
        const { res } = yield call(() =>
            suspendPropertyGuestRequest({ member, orgId, propertyId })
        );
        if (res) {
            yield put(
                openSnackbar({
                    open: true,
                    message: 'Success: Guest member has been updated!',
                    variant: 'alert',
                    alert: {
                        color: 'success'
                    }
                })
            );
        }
    } catch (error) {
        yield put(
            openSnackbar({
                open: true,
                message: `Failed: Guest member was not updated successfully.`,
                variant: 'alert',
                alert: {
                    color: 'error'
                }
            })
        );
        log(`Error: error suspending guest member (FS/RTDB)`, {
            error,
            member,
            orgId,
            propertyId
        });
    }
}

const queryMemberDataRequest = async ({ orgId, type, value }) => {
    const q =
        type === 'plate'
            ? query(
                  accesKeysCollectionRef,
                  where('org_id', '==', orgId),
                  where('plates', 'array-contains', value.trim())
              )
            : query(
                  devicesCollectionRef,
                  where('device_number', '==', value.trim()),
                  where('org_id', '==', orgId)
              );

    return getDocs(q)
        .then(querySnapshot => {
            const members = [];
            if (querySnapshot.empty) {
                return { members: [] };
            }
            querySnapshot.forEach(doc => {
                members.push(doc.data());
            });

            return { members };
        })
        .catch(error => {
            return { error };
        });
};

export function* queryMemberData({ payload }) {
    const { user, orgId, type, value } = payload;
    const { members, error } = yield call(() =>
        queryMemberDataRequest({ orgId, type, value })
    );
    if (members) {
        yield put(queryMemberDataSuccess(members));
    } else {
        yield put(queryMemberDataFailure(error));
        yield put(
            openSnackbar({
                open: true,
                message: `Failed: Advanced member search failed. Please try again later.`,
                variant: 'alert',
                alert: {
                    color: 'error'
                }
            })
        );
        log(`Error: error processing advanced member search (FS)`, {
            error,
            orgId,
            type,
            value,
            user
        });
    }
}

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////////////// Action Creators For Root Saga ////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function* checkingExistingMember() {
    yield takeLatest(GET_EXISTING_MEMBER, checkExistingMember);
}

export function* addingMember() {
    yield takeLatest(ADD_NEW_MEMBER, addMember);
}

export function* removingMember() {
    yield takeLatest(REMOVE_MEMBERS, removeMember);
}

export function* updatingMember() {
    yield takeLatest(UPDATE_MEMBER, updateMember);
}

export function* transferringMember() {
    yield takeLatest(TRANSFER_MEMBER, transferMemberSaga);
}

export function* removingPending() {
    yield takeLatest(REMOVE_PENDING, removePending);
}

export function* suspendingPropertyGuest() {
    yield takeLatest(SUSPEND_PROPERTY_GUEST, suspendPropertyGuest);
}

export function* queryingMemberData() {
    yield takeLatest(ADVANCE_MEMBER_QUERY, queryMemberData);
}

export default function* rootSaga() {
    yield all([
        fork(checkingExistingMember),
        fork(addingMember),
        fork(removingMember),
        fork(updatingMember),
        fork(transferringMember),
        fork(removingPending),
        fork(suspendingPropertyGuest),
        fork(queryingMemberData)
    ]);
}
